/**
 * Professional Plan Page
 *
 * This is not a classic subscription tier page.
 * Professional plan optimizes usage; it does not replace credits.
 * See /docs/professional_plan.md for specification.
 */

import * as React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCurrency } from "@/context/CurrencyContext";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Check,
  ArrowLeft,
  Coins,
  Shield,
  Zap,
  Clock,
  CreditCard,
  X,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import Sidebar from "@/components/layout/Sidebar";
import TopBar from "@/components/layout/TopBar";
import { subscriptionService } from "@/services/subscriptionService";
import { paymentService, SubscriptionPlan } from "@/services/paymentService";
import { toast } from "@/components/ui/use-toast";
import PaymentGatewaySelection from "@/components/payment/PaymentGatewaySelection";

export function ProfessionalPlanPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useSupabaseAuth();
  const [isSubscribing, setIsSubscribing] = React.useState(false);
  const [showCurrencyTip, setShowCurrencyTip] = React.useState(true);
  const [currentPlan, setCurrentPlan] = React.useState<string | null>(null);
  const [showPaymentDialog, setShowPaymentDialog] = React.useState(false);
  const [professionalPlan, setProfessionalPlan] =
    React.useState<SubscriptionPlan | null>(null);
  const { currency, formatPrice } = useCurrency();

  // Fetch current subscription status
  React.useEffect(() => {
    const fetchPlan = async () => {
      if (!user?.id) return;
      try {
        const subscription =
          await subscriptionService.getUserActiveSubscription(user.id);
        if (subscription && subscription.status === "active") {
          setCurrentPlan(subscription.plan_id);
        } else {
          setCurrentPlan("PAYG"); // Default to PAYG if no active subscription
        }
      } catch (error) {
        console.error("Error fetching subscription:", error);
        setCurrentPlan("PAYG"); // Default to PAYG on error
      }
    };
    fetchPlan();
  }, [user?.id]);

  const isProfessional = currentPlan === "professional";

  const handleSubscribe = async () => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to subscribe to the Professional plan.",
        variant: "destructive",
      });
      return;
    }

    // Create professional plan subscription object
    const planData: SubscriptionPlan = {
      id: "professional",
      name: "Professional Plan",
      price: 1165.0,
      currency: "ZAR",
      interval: "year" as const,
      features: [
        "100 credits/year",
        "Priority processing",
        "10-year predictions",
        "High-resolution imagery",
        "Extended historical data",
        "Priority support",
      ],
    };

    setProfessionalPlan(planData);
    setShowPaymentDialog(true);
  };

  const handlePaymentInitiated = (sessionId: string, redirectUrl?: string) => {
    setShowPaymentDialog(false);
    setProfessionalPlan(null);

    // Store session info for return handling
    if (typeof window !== "undefined") {
      localStorage.setItem("pendingPaymentSession", sessionId);
      localStorage.setItem("pendingPlanId", "professional");
      localStorage.setItem("pendingPlanName", "Professional Plan");
      localStorage.setItem("pendingPlanAmount", "1165.00");
      localStorage.setItem("pendingPlanCurrency", "ZAR");
    }

    toast({
      title: "Payment Initiated",
      description: "Redirecting to payment provider...",
    });

    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  };

  if (authLoading || currentPlan === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#40e0d0]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-gradient-to-b from-background to-background/80">
      <Sidebar />

      <div className="flex-1 flex flex-col">
        <TopBar
          title="Professional Plan"
          icon={<Shield className="h-4 w-4 text-[#40e0d0]" />}
          showBackButton={true}
        />

        <main className="flex-1 container mx-auto px-4 py-8 max-w-3xl">
          {/* Currency tip banner */}
          {showCurrencyTip && currency === "ZAR" && (
            <div
              className="running-glow-border mb-6"
              style={{ "--glow-bg": "rgba(0,0,0,0.9)" } as React.CSSProperties}
            >
              <Alert className="border-0 bg-transparent text-background dark:text-foreground font-semibold flex items-center justify-between">
                <AlertDescription className="flex-1">
                  Prices are shown in ZAR. You can switch to USD in the{" "}
                  <span className="font-bold text-teal-500">user menu</span>{" "}
                  <i>(top-right corner).</i>
                </AlertDescription>
                <button
                  onClick={() => setShowCurrencyTip(false)}
                  className="ml-4 shrink-0 rounded-sm opacity-70 hover:opacity-100 focus:outline-none"
                  aria-label="Dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              </Alert>
            </div>
          )}

          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold text-foreground mb-3">
              Professional Plan
            </h1>
            <p className="text-muted-foreground max-w-xl mx-auto">
              For users who rely on My!hu regularly. The Professional plan
              reduces friction and optimizes your workflow — credits remain the
              unit of value.
            </p>
          </div>

          {/* Pricing card */}
          <Card className="mb-8 border-[#40e0d0]/30">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-4xl font-bold">
                {formatPrice(1165.0)}
                <span className="text-lg font-normal text-muted-foreground ml-1">
                  / year
                </span>
              </CardTitle>
              <CardDescription className="text-base">
                Billed annually (equivalent to {formatPrice(97)}/month)
                {currency === "USD" && (
                  <span className="block text-xs opacity-70 mt-1">
                    * indicative, ~R18.50 per $1
                  </span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              {isProfessional ? (
                <div className="space-y-3">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 text-green-600">
                    <Check className="h-4 w-4" />
                    <span className="font-medium">
                      You're on the Professional plan
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Your subscription renews annually.
                  </p>
                </div>
              ) : (
                <Button
                  size="lg"
                  className="bg-[#40e0d0] text-black hover:bg-[#40e0d0]/90"
                  onClick={handleSubscribe}
                  disabled={false}
                >
                  Subscribe to Professional
                </Button>
              )}
            </CardContent>
          </Card>

          {/* How it works with credits */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Coins className="h-5 w-5 text-[#40e0d0]" />
                How Professional Works with Credits
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground font-semibold">
                The Professional plan does not replace credits — it enhances how
                you use them.
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-500 mt-6 flex-shrink-0" />
                  <div>
                    <span className="font-medium">Annual credits included</span>
                    <p className="text-sm text-muted-foreground">
                      Your plan includes 100 credits per year: 80 base credits
                      plus 20 bonus credits included with annual billing.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-500 mt-6 flex-shrink-0" />
                  <div>
                    <span className="font-medium">
                      Credits still work the same
                    </span>
                    <p className="text-sm text-muted-foreground">
                      All actions cost the same number of credits as PAYG.
                      Included credits simply reduce how often you need to
                      purchase more.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-500 mt-6 flex-shrink-0" />
                  <div>
                    <span className="font-medium">
                      Bonus credits are a reward for commitment
                    </span>
                    <p className="text-sm text-muted-foreground mb-2">
                      Bonus credits are added to your account at the start of
                      your annual term and are used first. They reset at renewal
                      and cannot be rolled over.{" "}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Benefits */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-lg flex items-start gap-2">
                <Zap className="h-5 w-5 text-[#40e0d0]" />
                What You Get
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 text-left ml-12">
                <ul className="list-disc what-you-get__list">
                  <li className="mb-3 what-you-get__item">
                    <strong>100 credits per year included</strong>
                    <br />
                    <span>
                      80 base credits + 20 bonus credits with annual billing
                    </span>
                  </li>

                  <li className="mb-3 what-you-get__item">
                    <strong>Priority processing</strong>
                    <br />
                    <span>
                      Faster generation for reports, predictions, and exports
                    </span>
                  </li>

                  <li className="mb-3 what-you-get__item">
                    <strong>10-year environmental predictions</strong>
                    <br />
                    <span>
                      Extended forward-looking climate and risk insights
                    </span>
                  </li>

                  <li className="mb-3 what-you-get__item">
                    <strong>High-resolution imagery access</strong>
                    <br />
                    <span>Enhanced spatial and satellite-based outputs</span>
                  </li>

                  <li className="mb-3 what-you-get__item">
                    <strong>Extended historical data (up to 20 years)</strong>
                    <br />
                    <span>
                      Deeper trend analysis and long-range comparisons
                    </span>
                  </li>

                  <li className="what-you-get__item">
                    <strong>Priority support</strong>
                    <br />
                    <span>Faster response times and dedicated assistance</span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Ownership note */}
          <Card className="mb-8 bg-muted/30">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium">Your outputs are yours</span>
                  <p className="text-sm text-muted-foreground">
                    All reports, predictions, and exports you generate are saved
                    permanently and remain accessible to you — whether or not
                    you stay on Professional.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Link to credits */}
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-3">
              Not ready for an annual commitment?
            </p>
            <Button variant="outline" asChild>
              <Link to="/purchase-credits">
                <Coins className="h-4 w-4 mr-2" />
                Purchase Credits Instead
              </Link>
            </Button>
          </div>
        </main>
      </div>

      {/* Payment Gateway Selection Dialog */}
      <Dialog
        open={showPaymentDialog}
        onOpenChange={(open) => {
          setShowPaymentDialog(open);
          if (!open) {
            setTimeout(() => setProfessionalPlan(null), 300);
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Complete Your Professional Subscription
            </DialogTitle>
            <DialogDescription className="sr-only">
              Select your preferred payment method to complete your Professional
              plan subscription
            </DialogDescription>
          </DialogHeader>
          {professionalPlan && (
            <PaymentGatewaySelection
              plan={professionalPlan}
              onPaymentInitiated={handlePaymentInitiated}
              isProcessing={isSubscribing}
              setIsProcessing={setIsSubscribing}
              onCloseDialog={() => setShowPaymentDialog(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ProfessionalPlanPage;
